/**
 * TextArea — the multi-line sibling of TextField (the wizard's optional note). Same anatomy: a real
 * `<label htmlFor>`, hint + error wired via `aria-describedby` / `aria-invalid`, RHF ref forwarded.
 */
import { forwardRef, useId, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: string;
  error?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, hint, error, id, className, ...props },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className="mt-3">
      <label htmlFor={inputId} className="mb-1.5 block text-[12px] font-medium text-ink-quiet">
        {label}
      </label>
      <div
        className={cn(
          'rounded-md border bg-surface px-3.5 py-1 transition-colors duration-ui ease-thread',
          'focus-within:border-sage focus-within:ring-2 focus-within:ring-sage-deep/20',
          error ? 'border-coral-deep' : 'border-line-2',
        )}
      >
        <textarea
          id={inputId}
          ref={ref}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            'min-h-[92px] w-full resize-none bg-transparent py-2 font-body text-[14px] text-ink outline-none',
            'placeholder:text-ink-quiet',
            className,
          )}
          {...props}
        />
      </div>
      {hint && !error && (
        <p id={hintId} className="mt-1.5 text-[11.5px] leading-snug text-ink-quiet">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="mt-1.5 text-[11.5px] font-medium leading-snug text-coral-text">
          {error}
        </p>
      )}
    </div>
  );
});
